import { useTranslation } from 'react-i18next';

import { Image } from '@codes/ui-kit/components/ui/image';
import { CardView } from '@lemoncloud/lemon-payments-api';
import { DeleteCardDialog } from '@web/app/features/my-page/components/DeleteCardDialog';
import { CreditCard, Trash2 } from 'lucide-react';

import { Badge } from '@codes/ui-kit';

import ErrorIcon from '/assets/icons/error.svg';

interface CardItemProps {
    card: CardView;
    onDefaultCardClick: (card: CardView) => void;
    onDeleteCard: (card: CardView) => void;
}

export const CardItem = ({ card, onDefaultCardClick, onDeleteCard }: CardItemProps) => {
    const { t } = useTranslation();

    const isExpired = () => {
        if (card.isValid) {
            return false;
        }
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;
        return card.expYear < currentYear || (card.expYear === currentYear && card.expMonth < currentMonth);
    };

    const expired = isExpired();

    return (
        <div
            className={`rounded-xl border p-4 flex items-center gap-4 max-xs:flex-col max-xs:items-start transition-colors ${
                expired ? 'border-error/20 bg-error/[0.02]' : 'border-border bg-card'
            }`}
        >
            {/* Card icon */}
            <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    expired ? 'bg-error/10' : 'bg-box'
                }`}
            >
                <CreditCard className={`w-5 h-5 ${expired ? 'text-error' : 'text-text-400'}`} />
            </div>

            {/* Card info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{card.brandEn || card.brand}</span>
                    <span className="text-text-400 text-sm">**** {card.last4}</span>
                    {expired && (
                        <Badge variant="destructive" className="text-[10px] h-5 px-1.5 gap-1">
                            <Image className="w-2.5" src={ErrorIcon} alt="" />
                            {t('myPage.unavailable')}
                        </Badge>
                    )}
                    {card.isDefault && (
                        <Badge className="text-[10px] h-5 px-1.5 bg-point/10 text-point border-0">
                            {t('myPage.default')}
                        </Badge>
                    )}
                </div>
                {expired && <div className="text-xs text-text-400 mt-1">{t('myPage.cardError')}</div>}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
                {!card.isDefault && !expired && (
                    <button
                        className="text-xs text-text-500 font-medium px-2.5 py-1.5 rounded-lg hover:bg-box transition-colors"
                        onClick={() => onDefaultCardClick(card)}
                    >
                        {t('myPage.setDefaultCard')}
                    </button>
                )}
                {(expired || !card.isDefault) && (
                    <DeleteCardDialog
                        trigger={
                            <button className="text-xs text-error/70 font-medium px-2.5 py-1.5 rounded-lg flex items-center gap-1 hover:bg-error/5 hover:text-error transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                                {t('myPage.deleteCard')}
                            </button>
                        }
                        onDelete={() => onDeleteCard(card)}
                    />
                )}
            </div>
        </div>
    );
};
